<template>
  <div>
    <exo-form hideClose :root="_self" :clear="false">
      <template v-slot:titleTooltip>
        <exo-help-dialog
          :color="'black--text'"
          margin="m-0"
          title="Maneuver Analysis Mission"
        >
          <tooltipManeuverAnalysisMission />
        </exo-help-dialog>
      </template>
      <exo-input-name :root="_self" />
      <v-row>
        <v-col cols="6">
          <exo-card-form label="Analysis">
            <maneuverAnalysisForm
              :analysisrequest.sync="inputs.analysisRequest"
              v-on:updateValidation="maneuverAnalysisValid = $event"
            />
          </exo-card-form>
          <template v-if="measurementsSelectorValid">
            <v-row v-if="updatingSummary" class="mt-3">
              <v-col cols="8"> Updating ... </v-col>
            </v-row>
            <template v-if="!updatingSummary">
              <v-row v-if="currentNbOfMeasurements >= 0" class="mt-3">
                <v-col cols="8">
                  Number of files corresponding to these criteria:
                </v-col>
                <v-col>
                  <strong>{{ currentNbOfMeasurements }}</strong>
                </v-col>
              </v-row>
              <v-row v-if="currentNbOfMeasurements >= 2" class="mt-3">
                <v-col cols="8"> Date of the oldest measurement: </v-col>
                <v-col>
                  <strong>{{ currentFirstDate.substring(0, 19) }}</strong>
                </v-col>
              </v-row>
              <v-row v-if="currentNbOfMeasurements >= 2" class="mt-3">
                <v-col cols="8"> Date of the latest measurement: </v-col>
                <v-col>
                  <strong>{{ currentLastDate.substring(0, 19) }}</strong>
                </v-col>
              </v-row>
              <v-row v-if="currentNbOfMeasurements === 1" class="mt-3">
                <v-col cols="8"> Date of the measurement: </v-col>
                <v-col>
                  <strong>{{ currentLastDate.substring(0, 19) }}</strong>
                </v-col>
              </v-row>
              <exo-form-alert
                v-if="!updatingSummary && currentNbOfMeasurements < 2"
                type="warning"
                class="mt-3"
              >
                Warning: At least 2 measurements are necessary to perform a
                maneuver restitution.
              </exo-form-alert>
            </template>
          </template>
          <v-row v-if="measurementsChartData.length > 0" class="mt-3">
            <v-col cols="11">
              <div ref="measurementTimelineChartDiv"></div>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="6">
          <exo-card-form label="Data selection">
            <v-row class="mt-5">
              <v-col>
                <measurementsSelectorForm
                  :measurementsselector.sync="inputs.measurementsSelector"
                  v-on:updateValidation="measurementsSelectorValid = $event"
                  :dataStartDate="currentFirstDate"
                  :dataEndDate="currentLastDate"
                />
              </v-col>
            </v-row>
          </exo-card-form>
          <exo-card-form label="Database management">
            <v-row class="mt-5">
              <v-col>
                <v-btn outlined v-on:click="goToImport()" small>
                  Import ECI files
                </v-btn>
                <exo-form-alert type="info" class="mt-3">
                  The import menu is generic to all missions. All selected
                  measurements will be saved and persistent in database. They
                  will be retrieved according to the NORAD ID selected in space
                  object.
                </exo-form-alert>
              </v-col>
            </v-row>
          </exo-card-form>
        </v-col>
      </v-row>
    </exo-form>
    <v-col ref="NumericalMissionErrorDisplay"> </v-col>
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import numericalMixin from '../../mixins/numericalForms.js'
import plotLayout from '../../mixins/plotLayout.js'
import Plotly from 'plotly.js-dist'
import dataTools from '../../tools/data.js'
import missionTools from '../../store/mission.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import { mapGetters } from 'vuex'
import moment from 'moment'
import Vue from 'vue'
import measurementsSelectorForm from './MeasurementsSelectorForm'
import maneuverAnalysisForm from './ManeuverAnalysisForm'
import tooltipManeuverAnalysisMission from '../../components/Tooltips/Defense/tooltipManeuverAnalysisMission'
import { element } from 'plotly.js-dist'

export default {
  name: 'maneuveranalysisrequest',

  mixins: [formMixin, numericalMixin, plotLayout],
  components: {
    measurementsSelectorForm,
    maneuverAnalysisForm,
    tooltipManeuverAnalysisMission
  },
  computed: {
    ...mapGetters(['missions', 'currentMission', 'currentUser']),
    inputsValid() {
      let fieldsState = this.verifyPropertiesList(
        this.getRequiredPropertiesList(this.inputs),
        this.inputs,
        true
      )
      fieldsState =
        fieldsState &&
        this.verifyPropertiesList(
          this.getOptionalPropertiesList(this.inputs),
          this.inputs,
          false
        )
      // if impossible to validate a form : uncomment the code in verifyPropertiesList to understand the problematic field
      return (
        this.inputNameState &&
        fieldsState &&
        this.maneuverAnalysisValid &&
        this.measurementsSelectorValid
      )
    },
    simulationIdsParam() {
      const measurementsSelector = this?.inputs?.measurementsSelector
      if (
        measurementsSelector?.measurementsType == null ||
        measurementsSelector?.simulationIds == null
      ) {
        return null
      }
      let simulationIds = []
      switch (measurementsSelector.measurementsType) {
        case 'GENERATED':
          simulationIds = measurementsSelector.simulationIds.slice()
          break
        case 'NONGENERATED':
          simulationIds.push('null')
          break
        default:
          simulationIds.push('null')
          simulationIds = simulationIds.concat(
            measurementsSelector.simulationIds.slice()
          )
      }
      return simulationIds.reduce(
        (currentIdsListString, id) => `${id},${currentIdsListString}`,
        ''
      )
    }
  },

  watch: {
    'inputs.analysisRequest': {
      handler(newValue) {
        if (newValue?.spaceObject?.noradID) {
          if (this.inputs.measurementsSelector != null) {
            this.inputs.measurementsSelector.noradID =
              newValue.spaceObject.noradID
          } else {
            this.inputs.measurementsSelector = {
              noradID: newValue.spaceObject.noradID,
              startDate: '',
              endDate: '',
              measurementsType: 'ALL',
              simulationIds: []
            }
          }
        }
      },
      immediate: true
    },
    'inputs.measurementsSelector': {
      handler(newValue) {
        this.updatingSummary = true
        this.measurementsChartData = []
        this.updateSummary().finally(() => {
          this.updatingSummary = false
        })
      },
      immediate: true
    },
    async measurementsChartData(data) {
      if (data.length > 0) {
        await this.$nextTick()
        this.refreshMeasurementsChart()
      }
    }
  },

  mounted() {
    this.refreshForm()
    this.$root.$on('current-mission-updated', this.currentMissionUpdatedHandler)
    this.$root.$on('copy-mission', this.copyMission)
    this.$root.$on('create-mission', this.createMission)
  },

  beforeDestroy() {
    this.$root.$off(
      'current-mission-updated',
      this.currentMissionUpdatedHandler
    )
    this.$root.$off('copy-mission', this.copyMission)
    this.$root.$off('create-mission', this.createMission)
  },

  data() {
    return {
      enumPropertiesList: this.getEnumProperties(),

      missionPlot: null,

      modalRef: { content: [] },
      showModal: false,
      maneuveringStrategies: {},

      importFileDialog: false,
      dataImporterDialog: false,

      inputs: {
        measurementsSelector: { noradID: '', startDate: '', endDate: '' },
        analysisRequest: null,
        editing: true,
        type: this.getDefaultValue('type'),
        layout: this.getDefaultValue('layout') // to save the dashboard, should be optional in fieldsdescription to be automatically copied
      },

      modalInfo: { title: '', content: '', config: {} },
      modalError: { title: '', content: '' },
      displayed: null,
      maneuverAnalysisValid: false,
      measurementsSelectorValid: false,
      enableFormCache: false,
      updatingSummary: false,
      currentNbOfMeasurements: -1,
      currentFirstDate: '',
      currentLastDate: '',
      measurementsChartData: []
    }
  },

  methods: {
    refreshTable() {},

    refreshMeasurementsChart() {
      const plotLayout = {
        plot_bgcolor: this.$vuetify.theme.dark ? '#1E1E1E' : 'white',
        paper_bgcolor: this.$vuetify.theme.dark ? '#1E1E1E' : 'white',
        font: { color: this.$vuetify.theme.dark ? 'white' : '#121212' },
        xaxis: {
          title: { text: 'Number of file per day' }
        },
        margin: { t: 0 },
        height: 170,
        shapes: this.maneuverHighlight,
        yaxis: {
          rangemode: 'tozero',
          title: {
            text: ``
          },
          dtick: 1
        }
      }

      if (this.$refs.measurementTimelineChartDiv) {
        Plotly.newPlot(
          this.$refs.measurementTimelineChartDiv,
          this.measurementsChartData,
          plotLayout,
          { displayModeBar: false, responsive: true }
        )
      }
    },

    getCurrentFormTitle() {
      return this.inputs.type + ' Mission'
    },

    createMission(mission) {
      if (mission && mission.typeName === this.getCurrentFormType()) {
        this.clearForm()
        this.inputs.type = mission.type
        this.inputs.editing = false
        this.sendFormToServer()
      }
    },

    currentMissionUpdatedHandler() {
      this.refreshForm()
    },

    copyMission(mission) {
      if (mission && mission.typeName === this.getCurrentFormType()) {
        this.copy(mission)
        this.sendFormToServer()
      }
    },

    clearLocalFields() {
      for (let field in this.getPropertiesDescription()) {
        if (this.inputs.hasOwnProperty(field)) {
          this.inputs[field] = this.getDefaultValue(field)
        }
      }
    },

    refreshForm() {
      if (
        this.currentMission != null &&
        this.currentMission.typeName === this.getCurrentFormType()
      ) {
        const editing = this.inputs.editing
        this.edit(this.currentMission, 0)
        this.inputs.editing = editing
      }
    },

    updateAllReferences() {
      this.inputs.editing = true
      this.checkReferences(this.currentMission, this.inputs)
    },

    checkReferences(missionSource, missionToUpdate) {
      if (missionSource != null && missionToUpdate != null) {
        for (let field in this.getPropertiesDescription()) {
          if (missionSource[field] != null && missionToUpdate[field] != null) {
            if (
              missionSource[field].id != null &&
              missionSource[field].id === missionToUpdate[field].id
            ) {
              missionToUpdate[field] = missionSource[field]
            }
          }
        }
      }
    },

    async updateSummary() {
      const params = {}
      const measurementSelector = this.inputs.measurementsSelector

      if (
        measurementSelector?.noradID != null &&
        measurementSelector?.noradID != ''
      ) {
        params.noradID = measurementSelector.noradID
      } else {
        this.currentNbOfMeasurements = -1
        this.currentFirstDate = ''
        this.currentLastDate = ''
        return
      }
      if (
        measurementSelector?.startDate != null &&
        measurementSelector?.startDate !== ''
      ) {
        params.startDate = measurementSelector.startDate
      }

      if (
        measurementSelector?.endDate != null &&
        measurementSelector?.endDate !== ''
      ) {
        params.endDate = measurementSelector.endDate
      }

      if (this.simulationIdsParam != null) {
        params.simulationIds = this.simulationIdsParam
      }

      const nbOfMeasurements = await this.updateCurrentNbOfMeasurements(params)

      if (nbOfMeasurements >= 1) {
        await this.updateMeasurementsChartData(params)
      } else {
        this.currentFirstDate = ''
        this.currentLastDate = ''
        this.measurementsChartData = []
      }
    },

    async updateCurrentNbOfMeasurements(params) {
      const nbOfMeasurements = await this.$http
        .get(`/users/${this.currentUser.id}/spmeasurements/count`, {
          params
        })
        .then((response) => response.data)
        .catch((e) => {
          this.displayMessage(
            'Error while getting measurements count.',
            e,
            'notification',
            'error'
          )
          return null
        })

      this.currentNbOfMeasurements = nbOfMeasurements
      return nbOfMeasurements
    },

    async updateMeasurementsChartData(params) {
      params.page = 1
      params.itemsPerPage = this.currentNbOfMeasurements
      params.sortBy = 'startDate'
      const responseData = await this.$http
        .get(`/users/${this.currentUser.id}/spmeasurements/getpage`, {
          params
        })
        .then((response) => response.data)
        .catch((e) => {
          this.displayMessage(
            'Error while getting measurements count.',
            e,
            'notification',
            'error'
          )
          return null
        })

      const currentChartDates = []
      const mList = responseData.measurementsList
      this.currentFirstDate = mList[0].startDate
      this.currentLastDate = mList[mList.length - 1].startDate

      if (mList.length == 1) {
        this.measurementsChartData = []
      } else {
        mList.forEach((element) => {
          currentChartDates.push(element.startDate.substr(0, 10))
        })

        const chartValues = []
        const chartLabels = []

        const startDate = moment.utc(this.currentFirstDate)
        const endDate = moment.utc(this.currentLastDate)
        const startDateShortIso = this.currentFirstDate.substring(0, 10)
        const endDateShortIso = this.currentLastDate.substring(0, 10)

        // ITERATE THROUGH ALL DAYS FROM START TO FINISH AND ADD DAILY MEASUREMENT COUNT TO VALUES
        for (
          var d = startDate;
          d.isSameOrBefore(endDate) ||
          d.format().substring(0, 10) === endDateShortIso;
          d.add(1, 'day')
        ) {
          const dayShortIsoString = d.format().substring(0, 10)
          let dailyCount = 0
          currentChartDates.forEach((element) => {
            if (element === dayShortIsoString) {
              dailyCount++
            }
          })
          chartValues.push(dailyCount)
          chartLabels.push(dayShortIsoString)
        }

        this.measurementsChartData = [
          {
            type: 'scatter',
            mode: 'lines',
            x: chartLabels,
            y: chartValues,
            line: { color: '#5757ff' }
          }
        ]
      }
      return this.measurementsChartData
    },

    sendFormToServer() {
      this.inputs.saving = true
      this.$http
        .post(this.getCurrentAPI(), this.createObjectFromForm())
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('set-current-mission', response.data)
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },
    // redefine from form.js to keep the form open on the upadted data
    async onSave() {
      this.inputs.saving = true
      this.$http
        .put(
          this.getCurrentAPI() + '/' + this.inputs.id,
          this.createObjectFromForm()
        )
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('update-mission-list')
          this.$root.$emit('set-current-mission', response.data) // update fields and current mission
          // redirect to dashboard
          this.$router.push({
            path:
              '/' +
              missionTools.missionUrlMapping[response.data.typeName].dashboard
          })
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },
    async onCreate() {
      let self = this
      let objectName = this.inputs.name.trim()
      this.inputs.saving = true
      this.$http
        .post(this.getCurrentAPI(), this.createObjectFromForm())
        .then((response) => {
          self.inputs.saving = false
          self.clearForm()
          this.$root.$emit('update-mission-list')
          this.$root.$emit('set-current-mission', response.data) // update fields and current mission
          // redirect to dashboard
          this.$router.push({
            path:
              '/' +
              missionTools.missionUrlMapping[response.data.typeName].dashboard
          })
        })
        .catch((e) => {
          self.inputs.saving = false
          self.displayMessage(
            'Error creating ' + objectName + '. ',
            e,
            'notification',
            'error'
          )
          throw e
        })
    },
    getResultFieldType(field) {
      return dataTools.getFieldTypeFromPath(
        ['maneuverReport', field],
        'SimulationResult',
        null
      )
    },

    getResultDefaultUnitLabel(field) {
      let fieldType = this.getResultFieldType(field)
      return (
        ' ' + dataTools.getUnitLabel(dataTools.getDefaultGuiUnit(fieldType))
      )
    },

    // methods to mange form & table like other components

    getCurrentFormType() {
      return 'ManeuverCatalogueAnalysisRequest'
    },

    goToImport() {
      let history = JSON.parse(localStorage.getItem('history') || '[]')
      let newHistoryObj = {
        routeName: this.getCurrentFormType(),
        id: this._uid
      }
      history.push(newHistoryObj)
      localStorage.setItem('history', JSON.stringify(history))
      this.$router.push('/maneuveranalysisdata')
      this.$root.$emit('current-mission-updated')
      Vue.nextTick(() => {
        this.$root.$emit('open-dialog')
      })
    }
  }
}
</script>
